import React, { Fragment, useEffect, useState } from "react";

import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";

import DataTable from "react-data-table-component";

import Spinner from "../../Common/Spinner";
import NoDataFound from "../../Common/NoDataFound";

import {
  customStyles,
} from "../../../Constant";

import {
  formatDate,
} from "../../Common/CommonFunction";

import AxiosInstance from "../../../services/AxiosInstance";

const PaymentHistory = () => {

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [perPage, setPerPage] = useState(10);

  const [totalRows, setTotalRows] = useState(0);

  const [sortColumn, setSortColumn] = useState("id");

  const [sortDirection, setSortDirection] = useState("DESC");

  /**
   * Fetch Payment History
   */
  const fetchPayments = async () => {

    try {

      setLoading(true);

      const response = await AxiosInstance().get(
        `/api/payment-history`,
        {
          params: {
            page: currentPage,
            limit: perPage,
            sort_by: sortColumn,
            sort_order: sortDirection,
          },
        }
      );

      console.log("PAYMENT RESPONSE => ", response);

      if (response?.data?.status) {

        setPayments(
          response?.data?.data || []
        );

        setTotalRows(
          response?.data?.pagination?.total_records || 0
        );

      } else {

        setPayments([]);
        setTotalRows(0);

      }

    } catch (error) {

      console.log("PAYMENT API ERROR => ", error);

      setPayments([]);
      setTotalRows(0);

    } finally {

      setLoading(false);

    }
  };

  /**
   * Initial Load
   */
  useEffect(() => {

    fetchPayments();

  }, [
    currentPage,
    perPage,
    sortColumn,
    sortDirection
  ]);

  /**
   * Pagination
   */
  const handlePageChange = (page) => {

    setCurrentPage(page);

  };

  /**
   * Per Page
   */
  const handlePerRowsChange = (
    newPerPage,
    page
  ) => {

    setPerPage(newPerPage);

    setCurrentPage(page);

  };

  /**
   * Sorting
   */
  const handleSort = (
    column,
    direction
  ) => {

    setSortColumn(
      column.sortField || "id"
    );

    setSortDirection(
      direction.toUpperCase()
    );

  };

  /**
   * Payment Status Badge
   */
  const getStatusBadge = (status) => {

    switch (status) {

      case "paid":
        return "success";

      case "failed":
        return "danger";

      case "pending":
        return "warning";

      default:
        return "secondary";
    }
  };

  /**
   * Billing Badge
   */
  const getBillingBadge = (cycle) => {

    switch (cycle) {

      case "monthly":
        return "primary";

      case "yearly":
        return "info";

      default:
        return "secondary";
    }
  };

  /**
   * Table Columns
   */
  const columns = [

    {
      name: "#",
      width: "70px",
      cell: (row, index) =>
        (currentPage - 1) * perPage + index + 1,
    },

    {
      name: "Plan",
      selector: (row) =>
        row.plan_name || "-",

      sortable: true,

      sortField: "plan_name",

      minWidth: "170px",

      cell: (row) => (
        <Badge color="primary" pill>
          {row.plan_name || "N/A"}
        </Badge>
      ),
    },

    {
      name: "Billing",
      selector: (row) =>
        row.billing_cycle || "-",

      sortable: true,

      sortField: "billing_cycle",

      center: true,

      cell: (row) => (
        <Badge
          color={getBillingBadge(
            row.billing_cycle
          )}
          pill
        >
          {(row.billing_cycle || "-")
            .toUpperCase()}
        </Badge>
      ),
    },

    {
      name: "Amount",
      selector: (row) =>
        row.amount || 0,

      sortable: true,

      sortField: "amount",

      center: true,

      cell: (row) => (
        <span className="fw-bold text-dark">
          ₹{parseFloat(
            row.amount || 0
          ).toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      name: "Discount",
      selector: (row) =>
        row.discount_amount || 0,

      sortable: true,

      sortField: "discount_amount",

      center: true,

      cell: (row) => (
        <span className="fw-bold text-success">
          ₹{parseFloat(
            row.discount_amount || 0
          ).toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      name: "Coupon",
      selector: (row) =>
        row.coupon_code || "-",

      sortable: true,

      sortField: "coupon_code",

      center: true,

      cell: (row) => (

        row.coupon_code ? (

          <Badge color="dark">
            {row.coupon_code}
          </Badge>

        ) : "-"

      ),
    },

    {
      name: "GST",
      selector: (row) =>
        row.gst_amount || 0,

      sortable: true,

      sortField: "gst_amount",

      center: true,

      cell: (row) => (
        <span className="fw-bold text-warning">
          ₹{parseFloat(
            row.gst_amount || 0
          ).toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      name: "Total Paid",
      selector: (row) =>
        row.total_amount || 0,

      sortable: true,

      sortField: "total_amount",

      center: true,

      minWidth: "140px",

      cell: (row) => (
        <span
          className="fw-bold"
          style={{
            color: "#056cb8",
            fontSize: "14px",
          }}
        >
          ₹{parseFloat(
            row.total_amount || 0
          ).toLocaleString("en-IN")}
        </span>
      ),
    },

    {
      name: "Status",
      selector: (row) =>
        row.payment_status || "-",

      sortable: true,

      sortField: "payment_status",

      center: true,

      cell: (row) => (
        <Badge
          color={getStatusBadge(
            row.payment_status
          )}
          pill
        >
          {(row.payment_status || "-")
            .toUpperCase()}
        </Badge>
      ),
    },

    {
      name: "Paid On",
      selector: (row) =>
        row.created_at || "-",

      sortable: true,

      sortField: "created_at",

      center: true,

      minWidth: "170px",

      cell: (row) =>
        row.created_at
          ? formatDate(row.created_at)
          : "-",
    },
  ];

  return (
    <Fragment>

      <Row>

        <Col xs="12">

          <Card>

            <CardHeader className="py-3">

              <h5 className="mb-0">
                Payment History
              </h5>

            </CardHeader>

            <CardBody className="px-0 py-3">

              <DataTable
                customStyles={customStyles}
                data={payments}
                columns={columns}
                striped
                responsive
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={
                  handlePerRowsChange
                }
                progressPending={loading}
                progressComponent={<Spinner />}
                noDataComponent={
                  <NoDataFound
                    message="No payment history found"
                  />
                }
                onSort={handleSort}
                persistTableHead
              />

            </CardBody>

          </Card>

        </Col>

      </Row>

    </Fragment>
  );
};

export default PaymentHistory;